import { USAGE_UNLIMITED } from '@gonfalon/billing/constants';
import { hasSubscriptionLimit } from '@gonfalon/billing/utils';
import numbro from 'numbro';

import { DataListItem, Flex, type RequestError } from '../../shared';
import { Box } from '../../shared/components';
import { getUsagePercentage } from '../model';

import { SubscriptionUsageMeter } from './SubscriptionUsageMeter';

import styles from './BillingLimitRow.module.css';

function formatNumber(value: number): string {
  return numbro(value).format({ thousandSeparated: true, mantissa: 3, trimMantissa: true });
}

function formatLimit(limit: number): string {
  if (limit === USAGE_UNLIMITED) {
    return '∞';
  }
  return numbro(limit).format({ average: true, mantissa: 2, trimMantissa: true }).toUpperCase();
}

export function summarizeSubscriptionUsage(
  usage: number | undefined,
  limit: number,
  isLoading?: boolean,
  error?: RequestError,
) {
  if (!hasSubscriptionLimit(limit)) {
    return null;
  }

  const formattedLimit = formatLimit(limit);

  if (error) {
    return `Error loading data / ${formattedLimit}`;
  }
  if (isLoading) {
    return `Loading... / ${formattedLimit}`;
  }
  if (usage === null || usage === undefined) {
    return `Limit: ${formattedLimit}`;
  }

  const percentage = Math.round(getUsagePercentage(usage, limit) * 100);
  return `${formatNumber(usage)} / ${formattedLimit} (${percentage}%)`;
}

type BillingLimitRowProps = {
  title: string;
  /** The subscription limit value. */
  limit: number;
  /**
   * The current usage value from the usage endpoint.
   * `undefined` means the metric is not tracked in the usage endpoint
   * in which case only the limit is shown.
   */
  usage: number | undefined;
  isLoading: boolean;
  error?: RequestError;
};

export function BillingLimitRow({ title, limit, usage, isLoading, error }: BillingLimitRowProps) {
  if (!hasSubscriptionLimit(limit)) {
    return null;
  }

  return (
    <DataListItem>
      <Flex alignItems="center">
        <Box width={1 / 3} className={styles.title}>
          {title}
        </Box>
        <Box width={1 / 3} className={styles.meter}>
          {((usage !== null && usage !== undefined) || isLoading || error) && (
            <SubscriptionUsageMeter ratio={getUsagePercentage(usage ?? 0, limit)} isLoading={isLoading} error={error} />
          )}
        </Box>
        <Box ml="auto" className={styles.summary}>
          {summarizeSubscriptionUsage(usage, limit, isLoading, error)}
        </Box>
      </Flex>
    </DataListItem>
  );
}
